/* RESPONSIBLE TEAM: team-knowledge-foundations */

import { action } from '@ember/object';
import Service from '@ember/service';
import { inject as service } from '@ember/service';
import type Router from '@ember/routing/router-service';
import { EditorMode } from 'embercom/components/knowledge-hub/content-editor/drawer-editor';
import type KnowledgeHubEditorService from './knowledge-hub-editor-service';
import type IntercomConfirmService from './intercom-confirm-service';
import type IntlService from 'ember-intl/services/intl';
import type Controller from '@ember/controller';
import { trackedInLocalStorage } from 'ember-tracked-local-storage';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import { isBlank } from '@ember/utils';
import type ContentSnippet from 'embercom/models/content-service/content-snippet';
import type InternalArticle from 'embercom/models/content-service/internal-article';
import type ArticleContent from 'embercom/models/articles/article-content';
import Evented from '@ember/object/evented';
import type SuggestionService from './suggestion-service';
import { tracked } from '@glimmer/tracking';
import { taskFor } from 'ember-concurrency-ts';
import { ReviewType } from 'embercom/models/content-service/content-review-request';

export const DRAWER_EDITOR_QUERY_PARAMS = [
  'activeContentId',
  'activeContentType',
  'editorMode',
  'folderId',
  'collectionId',
  'contentLocale',
  'fromConDupeSuggestion',
];

const DRAWER_EDITOR_FULLSCREEN_KEY = 'is_drawer_editor_fullscreen';

export default class KnowledgeHubDrawerEditorService extends Service.extend(Evented) {
  @service declare router: Router;
  @service declare intl: IntlService;
  @service declare knowledgeHubEditorService: KnowledgeHubEditorService;
  @service declare intercomConfirmService: IntercomConfirmService;
  @service declare suggestionService: SuggestionService;
  @service declare notificationsService: $TSFixMe;
  @tracked isClosingDrawer = false;

  @trackedInLocalStorage({ keyName: DRAWER_EDITOR_FULLSCREEN_KEY, defaultValue: false })
  isFullScreen = false;
  isNewContent = false;

  get editorMode(): EditorMode | null {
    if (this.router?.currentRoute?.queryParams?.editorMode) {
      return this.router.currentRoute.queryParams.editorMode as EditorMode;
    }

    return null;
  }

  get fromConDupeSuggestion() {
    return this.router?.currentRoute?.queryParams?.fromConDupeSuggestion;
  }

  @action
  toggleFullScreen() {
    this.isFullScreen = !this.isFullScreen;
  }

  @action
  async closeDrawer() {
    this.isClosingDrawer = true;

    try {
      if (this.isNewContent && this.isEmptyContent()) {
        await this.knowledgeHubEditorService.deleteActiveContent();
        this.triggerOnDrawerClose('delete');
        this.removeQueryParams();
        return;
      }

      if (!this.hasDirtyActiveContent() && !this.suggestionService.hasDirtyContent) {
        this.triggerOnDrawerClose('close');
        this.removeQueryParams();
        return;
      }

      if (this.editSnippetHasPendingReviewRequest()) {
        await this.handleCancelOnClose();
        return;
      }

      let confirmationResult = await this.confirmClose();
      if (confirmationResult === true) {
        await this.handleSaveOnClose();
      } else if (confirmationResult.canceled) {
        await this.handleCancelOnClose();
      }
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.content-editor.drawer-editor.close-modal-error-message'),
      );
      this.triggerOnDrawerClose('close');
      this.removeQueryParams();
    } finally {
      this.isClosingDrawer = false;
    }
  }

  private editSnippetHasPendingReviewRequest(): boolean {
    return (
      this.editorMode === EditorMode.REVIEW &&
      this.knowledgeHubEditorService.activeContentIsContentSnippet &&
      this.suggestionService.reviewRequests.firstObject?.get('reviewType') ===
        ReviewType.EDIT_CONTENT &&
      !this.suggestionService.allReviewRequestsAreActioned
    );
  }

  private async handleSaveOnClose() {
    try {
      await this.performContentAction('save');
      this.triggerOnDrawerClose('save');
      this.removeQueryParams();
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.content-editor.edit.save-error-message'),
      );
      throw error;
    }
  }

  private async handleCancelOnClose() {
    try {
      await this.performContentAction('cancel');
      this.triggerOnDrawerClose('cancel');
      this.removeQueryParams();
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.content-editor.edit.cancel-error-message'),
      );
      this.triggerOnDrawerClose('cancel');
      this.removeQueryParams();
    }
  }

  isEmptyContent() {
    let activeContent = this.knowledgeHubEditorService.activeContentModel;
    if (!activeContent) {
      return false;
    }
    let title = activeContent.title;
    let isDefaultTitle = isBlank(title) || title === activeContent.defaultTitle;

    if (this.knowledgeHubEditorService.activeContentIsContentSnippet) {
      let contentSnippet = activeContent as ContentSnippet;
      let hasBody = contentSnippet.jsonBlocksHasContent;
      return isDefaultTitle && !hasBody;
    }

    if (this.knowledgeHubEditorService.activeContentIsInternalArticle) {
      let internalArticle = activeContent as InternalArticle;
      let hasBody =
        internalArticle.currentVersion?.jsonBlocks &&
        internalArticle.currentVersion.jsonBlocks.length > 0;
      return isDefaultTitle && !hasBody;
    }

    if (this.knowledgeHubEditorService.activeContentIsPublicArticle) {
      let articleContent = activeContent as unknown as ArticleContent;
      let hasBody = articleContent.jsonBlocks && articleContent.jsonBlocks.length > 0;
      return isDefaultTitle && !hasBody;
    }

    return false;
  }

  @action
  openViewDrawer({
    activeContentId,
    activeContentType,
    routeName,
    newTab = false,
    queryParams,
  }: {
    activeContentId: number | string;
    activeContentType: string;
    routeName?: string;
    newTab?: boolean;
    queryParams?: Record<string, unknown>;
  }) {
    this.knowledgeHubEditorService.trackAnalyticsEvent(
      'view',
      'knowledge_hub_in_context_editor',
      activeContentId,
      activeContentType,
    );
    this.isNewContent = false;

    this.knowledgeHubEditorService.openActiveContent(
      activeContentId,
      activeContentType,
      EditorMode.VIEW,
      routeName,
      newTab,
      queryParams,
    );
  }

  @action
  openEditDrawer({
    activeContentId,
    activeContentType,
    routeName,
    newTab = false,
    queryParams,
  }: {
    activeContentId: number | string;
    activeContentType: string;
    routeName?: string;
    newTab?: boolean;
    queryParams?: Record<string, unknown>;
  }) {
    this.knowledgeHubEditorService.trackAnalyticsEvent(
      'edit',
      'knowledge_hub_in_context_editor',
      activeContentId,
      activeContentType,
    );
    this.knowledgeHubEditorService.openActiveContent(
      activeContentId,
      activeContentType,
      EditorMode.EDIT,
      routeName,
      newTab,
      queryParams,
    );
  }

  @action
  openReviewDrawer({
    activeContentId,
    activeContentType,
    routeName,
    newTab = false,
    queryParams,
  }: {
    activeContentId: number | string;
    activeContentType: string;
    routeName?: string;
    newTab?: boolean;
    queryParams?: Record<string, unknown>;
  }) {
    this.knowledgeHubEditorService.trackAnalyticsEvent(
      'review',
      'knowledge_hub_in_context_editor',
      activeContentId,
      activeContentType,
    );
    this.isNewContent = false;

    this.knowledgeHubEditorService.openActiveContent(
      activeContentId,
      activeContentType,
      EditorMode.REVIEW,
      routeName,
      newTab,
      queryParams,
    );
  }

  @action
  createNewContent({
    activeContentType,
    collectionId,
    contentLocale,
    folderId,
    folder_id,
    routeName,
    openInNewTab,
  }: {
    activeContentType: string;
    collectionId?: string;
    contentLocale?: string;
    folderId?: string;
    folder_id?: string;
    routeName?: string;
    openInNewTab?: boolean;
  }) {
    folderId = folderId ?? folder_id;

    this.isNewContent = true;

    let queryParams = {
      editorMode: EditorMode.NEW,
      activeContentType,
      ...(collectionId ? { collectionId } : {}),
      ...(contentLocale ? { contentLocale } : {}),
      ...(folderId ? { folderId } : {}),
    };

    if (openInNewTab && routeName) {
      safeWindowOpen(this.router.urlFor(routeName, { queryParams }), '_blank');
    } else if (routeName) {
      this.router.transitionTo(routeName, {
        queryParams,
      });
    } else {
      this.router.transitionTo({
        queryParams,
      });
    }
  }

  hasDirtyActiveContent() {
    if (this.knowledgeHubEditorService.activeContentModel) {
      return this.knowledgeHubEditorService.activeContentModel.hasUnsavedChanges;
    }

    return false;
  }

  confirmClose() {
    return this.intercomConfirmService.confirm({
      title: this.intl.t('knowledge-hub.content-editor.drawer-editor.confirmation-dialog.title'),
      body: this.intl.t('knowledge-hub.content-editor.drawer-editor.confirmation-dialog.body'),
      confirmButtonText: this.intl.t(
        'knowledge-hub.content-editor.drawer-editor.confirmation-dialog.confirmButtonText',
      ),
      cancelButtonText: this.intl.t(
        'knowledge-hub.content-editor.drawer-editor.confirmation-dialog.cancelButtonText',
      ),
      distinguishCancelFromClose: true,
    });
  }

  // This is a workaround to remove the drawer editors query params when transitioning to a new route
  // This is needed because query params in ember are sticky and not being removed when transitioning to a new route
  // When you transition away and come back, the query params are still there and the drawer is opened (which is not what we want)
  // https://guides.emberjs.com/v3.7.0/routing/query-params/#toc_sticky-query-param-values
  removeEmberStickyQueryParams(
    controller: Controller & {
      activeContentId?: number;
      activeContentType?: string;
      editorMode?: EditorMode;
    },
  ) {
    controller.set('activeContentId', null);
    controller.set('activeContentType', null);
    controller.set('editorMode', null);
  }

  private removeQueryParams() {
    this.knowledgeHubEditorService.trackAnalyticsEvent('close', 'knowledge_hub_in_context_editor');
    this.isNewContent = false;
    this.router.transitionTo({
      queryParams: {
        activeContentId: null,
        activeContentType: null,
        editorMode: null,
        collectionId: null,
        contentLocale: null,
        folderId: null,
      },
    });
  }

  private triggerOnDrawerClose(action: 'delete' | 'close' | 'save' | 'cancel') {
    let queryParams = this.router.currentRoute?.queryParams ?? {};
    this.trigger('onDrawerClose', { ...queryParams, action });
  }

  async performContentAction(action: 'save' | 'cancel'): Promise<void> {
    if (action === 'save') {
      await this.performSaveOperation();
    } else {
      await this.performCancelOperation();
    }
  }

  private async performSaveOperation(): Promise<void> {
    if (this.editorMode === EditorMode.REVIEW) {
      await this.performReviewModeSave();
    } else {
      await this.knowledgeHubEditorService.saveActiveContent({ redirectToView: false });
    }
  }

  private async performReviewModeSave(): Promise<void> {
    if (this.knowledgeHubEditorService.activeContentIsContentSnippet) {
      await this.performContentSnippetSave();
    } else {
      await this.performArticleSave();
    }
    await this.refreshSuggestionsAndSummary();
  }

  private async performContentSnippetSave(): Promise<void> {
    let isCreateContent =
      this.suggestionService.reviewRequests.firstObject?.get('reviewType') ===
      ReviewType.CREATE_CONTENT;

    if (isCreateContent) {
      await this.saveCreateContentSnippet();
    } else {
      await this.saveEditContentSnippet();
    }
  }

  private async saveCreateContentSnippet(): Promise<void> {
    await taskFor(this.suggestionService.performSaveSnippetContent).perform({
      isAutosavingContent: false,
      isSavingContent: true,
    });
    await this.suggestionService.reloadContent({ isSaveAsDraft: false });
  }

  private async saveEditContentSnippet(): Promise<void> {
    await taskFor(this.suggestionService.saveSnippetTask).perform();
    await this.suggestionService.reloadContent({ isSaveAsDraft: false });
  }

  private async performArticleSave(): Promise<void> {
    await taskFor(this.suggestionService.saveArticleAsDraftTask).perform();
    await this.suggestionService.reloadContent({ isSaveAsDraft: true });
  }

  private async refreshSuggestionsAndSummary(): Promise<void> {
    await this.suggestionService.contentSuggestionsService.refreshSuggestionsListWithDelay();
    await this.suggestionService.contentImportService.fetchContentReviewSummary(true);
  }

  private async performCancelOperation(): Promise<void> {
    if (this.editorMode === EditorMode.REVIEW) {
      this.knowledgeHubEditorService.cancelEditActiveContent({ redirectToView: false });
      await this.suggestionService.reloadContent({ isCancel: true });
    } else {
      this.knowledgeHubEditorService.cancelEditActiveContent({ redirectToView: false });
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    knowledgeHubDrawerEditorService: KnowledgeHubDrawerEditorService;
    'knowledge-hub-drawer-editor-service': KnowledgeHubDrawerEditorService;
  }
}
